import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { PropsWithChildren } from 'react';
import type { Session } from '@supabase/supabase-js';

import { hasSupabaseClient, supabase } from './supabase-client';

export type Member = {
  id: string;
  user_id: string;
  organization_id: string;
  status: 'active' | 'inactive' | 'pending';
  profiles: {
    display_name: string;
    demo_alias: string | null;
    email: string;
    status: string;
  } | null;
  organization_roles: {
    key: string;
    label: string;
    is_owner: boolean;
    is_secretary: boolean;
  } | null;
};

type AuthContextValue = {
  session: Session | null;
  member: Member | null;
  loading: boolean;
  unavailable: boolean;
  refresh: () => Promise<void>;
  signOut: (allSessions?: boolean) => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const memberFetchFailed = Symbol('member-fetch-failed');

async function fetchMember(userId: string): Promise<Member | null | typeof memberFetchFailed> {
  if (!supabase) return null;
  const { data, error } = await supabase
    .from('organization_members')
    .select(
      'id,user_id,organization_id,status,profiles(display_name,demo_alias,email,status),organization_roles(key,label,is_owner,is_secretary)',
    )
    .eq('user_id', userId)
    .eq('status', 'active')
    .maybeSingle();
  if (error) return memberFetchFailed;
  return (data as Member | null) ?? null;
}

export function AuthProvider({ children }: PropsWithChildren) {
  const [session, setSession] = useState<Session | null>(null);
  const [member, setMember] = useState<Member | null>(null);
  const [loading, setLoading] = useState(hasSupabaseClient);

  const refresh = async () => {
    if (!supabase) return;
    const { data } = await supabase.auth.getSession();
    setSession(data.session);
    const next = data.session ? await fetchMember(data.session.user.id) : null;
    if (next !== memberFetchFailed) setMember(next);
    setLoading(false);
  };

  useEffect(() => {
    if (!supabase) return;
    void refresh();
    const { data: subscription } = supabase.auth.onAuthStateChange((_, nextSession) => {
      setSession(nextSession);
      if (!nextSession) {
        setMember(null);
        return;
      }
      window.setTimeout(() => {
        void fetchMember(nextSession.user.id).then((next) => {
          if (next !== memberFetchFailed) setMember(next);
        });
      }, 0);
    });
    return () => subscription.subscription.unsubscribe();
  }, []);

  const signOut = async (allSessions = false) => {
    if (!supabase) return;
    await supabase.rpc('log_auth_event', { event_action: 'auth.signed_out', event_origin: 'web' });
    await supabase.auth.signOut({ scope: allSessions ? 'global' : 'local' });
  };

  const value = useMemo(
    () => ({ session, member, loading, unavailable: !hasSupabaseClient, refresh, signOut }),
    [session, member, loading],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth doit être utilisé dans AuthProvider.');
  return context;
}
